import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, merge, Observable, of, Subject } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { InteractionTrackingService } from "../interaction-tracking.service";
import { NovaCompanionSettingsService } from "../nova-companion-settings.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { CoreJourneyService } from "./core-journey.service";
import { H01_LESSON_ID, H01HomeIntroductionService } from "./h01-home-introduction.service";
import { LearnOrientationLesson, LearnOrientationPrompt } from "./learn-orientation-lesson";
import { isOnHome } from "./onboarding-lesson-modal";

export const N01_LESSON_ID = "N01";

/** Recorded with N01's completed lesson state - N01's CompletedAt *is* this milestone. */
export const NOVA_LAUNCHER_INTRODUCED = "nova_launcher_introduced";

/** Anchors workbook: the Nova launcher on Home (the FAB / mobile tab). */
export const N01_ANCHOR = "nova.launcher";

/** Copy rows N01.01-N01.02, workbook v2.38 Copy sheet (verbatim). */
export const N01_COPY = {
  heading: "Meet Nova",
  body:
    "Nova is your flip* companion. Share what’s on your mind, explore what may be affecting your " +
    "capacity, or ask for help finding your way around flip*.",
};

/**
 * Onboarding lesson N01 "Meet Nova" (FLIP-388; spec v2.36 §24.1, workbook v2.38): an informational
 * anchored prompt at the Nova launcher on Home. Flow shared with the other Nova lessons - see
 * LearnOrientationLesson.
 *
 * - Eligible on the first Home visit after core onboarding is complete (the core-journey predicate;
 *   until then no prompt shows, and the next Home visit checks again) and the Home walkthrough (H01)
 *   has resolved: H01 completed or dismissed, or suppressed by Score history. While H01 is still
 *   open, N01 waits for it to close.
 * - Nova is never opened by the lesson. Completes on the visible close, or when Nova is opened
 *   while the prompt is visible.
 * - Nova opened independently (before the prompt ever showed) suppresses N01 permanently.
 * - Leaving Home with the prompt showing resolves it (workbook v2.50: `exit_resolves = YES`; the host
 *   calls `homeLeft()`). Leaving before the prompt has shown - to complete a Score first, say -
 *   records nothing, so it is deferred to the next Home visit.
 * - Not shown while Nova Companion is disabled.
 */
@Injectable({ providedIn: "root" })
export class N01MeetNovaService extends LearnOrientationLesson {
  protected readonly stepKey = N01_LESSON_ID;
  protected readonly milestone = NOVA_LAUNCHER_INTRODUCED;

  private companionEnabled = true;
  private suppressedThisSession = false;
  /** Emits when Nova is opened independently and N01 is permanently suppressed (see settled$). */
  private readonly suppressed$ = new Subject<void>();

  constructor(
    router: Router,
    coachMarkService: CoachMarkService,
    blockingUiSignal: BlockingUiSignalService,
    private readonly flags: FeatureFlagService,
    paymentsService: PaymentsService,
    private readonly steps: OnboardingStepService,
    interactionTracking: InteractionTrackingService,
    private readonly h01: H01HomeIntroductionService,
    private readonly companionSettings: NovaCompanionSettingsService,
    private readonly coreJourney: CoreJourneyService
  ) {
    super(router, coachMarkService, blockingUiSignal, flags, paymentsService, steps, interactionTracking);
    companionSettings.enabled$.subscribe((enabled) => (this.companionEnabled = enabled));
    paymentsService.subscriptionActive$.subscribe((active) => {
      if (!active) {
        this.suppressedThisSession = false;
      }
    });
  }

  /** Call on each Home arrival. Home has nothing to wait for beyond H01, so it is ready at once. */
  homeEntered(): void {
    this.enter();
    this.setReady(true);
  }

  /**
   * Call when Home is left. Completes N01 if its prompt is on screen (`exit_resolves = YES`), so it
   * counts as settled for BSB01; if the prompt has not shown, nothing is recorded and it is deferred
   * to the next Home visit. Either way the Home entry is dropped.
   */
  homeLeft(): void {
    this.onTargetUsed("leave_home");
    this.leave();
  }

  /**
   * Call whenever Nova is opened (the launcher, the header badge). Completes N01 if its prompt is
   * on screen; otherwise Nova has been opened independently, so N01 is suppressed permanently.
   */
  onNovaOpened(): void {
    if (this.isShowing) {
      this.onTargetUsed("open_nova");
      return;
    }
    if (this.isCompleted || this.suppressedThisSession || !this.flags.isEnabled(FeatureFlagKeys.OnboardingWalkthrough)) {
      return;
    }
    this.suppressedThisSession = true;
    this.steps.upsertStep({ stepKey: N01_LESSON_ID, dismissed: true });
    this.suppressed$.next();
  }

  /**
   * The derived signal `n01_settled` (FLIP-503; workbook v2.50 Signals, consumer BSB01): emits `true`
   * once, as soon as N01 is no longer pending - completed or dismissed (on this visit or an earlier
   * one), resolved by opening Nova or by leaving Home while it was showing (`homeLeft()`), suppressed
   * because Nova was opened independently, or permanently suppressed because Nova Companion is
   * disabled. Never emits while N01's prompt is on screen, and never writes N01 state itself.
   * Needs N01's own entry (`homeEntered()`) to have started on this Home visit: that is what resolves
   * `done$` when the prompt closes. A failed read emits nothing, so the consumer holds back.
   */
  settled$(): Observable<true> {
    const persisted$ = this.steps.getStep(N01_LESSON_ID).pipe(
      take(1),
      filter((step) => !!step && (!!step.completedAt || !!step.dismissed)),
      map((): true => true)
    );
    const closed$ = this.done$.pipe(
      filter((done) => done),
      map((): true => true)
    );
    const companionDisabled$ = this.companionSettings.enabled$.pipe(
      filter((enabled) => !enabled),
      map((): true => true)
    );
    const suppressed$ = this.suppressed$.pipe(map((): true => true));
    return merge(persisted$, closed$, companionDisabled$, suppressed$).pipe(take(1));
  }

  /**
   * Core onboarding must be complete (a failed check holds back); then H01 must have resolved - if
   * it is open right now, wait for it to close.
   */
  protected eligible$(): Observable<boolean> {
    return this.coreJourney.status().pipe(
      take(1),
      switchMap((core) => (core?.complete ? this.h01Resolved$() : of(false)))
    );
  }

  private h01Resolved$(): Observable<boolean> {
    return forkJoin([this.steps.getStep(H01_LESSON_ID), this.steps.hasSubmittedScore()]).pipe(
      switchMap(([h01, hasScore]) => {
        if (h01 === undefined || hasScore === null) {
          return of(false); // couldn't check - hold back rather than guess
        }
        const resolved = !!h01?.completedAt || !!h01?.dismissed || hasScore || this.h01.completedThisSession;
        return resolved ? of(true) : this.h01.completed$.pipe(take(1), map(() => true));
      })
    );
  }

  protected prompt(): LearnOrientationPrompt | null {
    if (!this.companionEnabled) {
      return null;
    }
    return { lessonId: N01_LESSON_ID, target: N01_ANCHOR, ...N01_COPY };
  }

  protected isOnSurface(): boolean {
    return isOnHome(this.router);
  }
}
